import { executeDynamicInlineScript } from '../../../utils/script.utils';
import {
  CompiledTemplate,
  CompileOpts,
  NunjucksCompiler,
  TemplateMetadata,
} from './NunjucksCompilerApi';
import nunjucks from 'nunjucks';

// key in window where compiled templates are loaded temporarily
const WINDOW_KEY = '_nunjucksInlineßScriptCompiled';

// using this as example: https://github.com/mozilla/nunjucks/blob/master/nunjucks/src/precompile-global.js
// called after templates are precompiled, wrapping each template in a function that assigns it to a global object
function wrapperFn(templates: any) {
  const templateLoadersAsCode = [];
  for (let i = 0; i < templates.length; i++) {
    const name = JSON.stringify(templates[i].name);
    const template = templates[i].template;

    const templateLoaderCode = `(function() {(window.${WINDOW_KEY} = window.${WINDOW_KEY} || {})[${name}] = (function() {
    ${template}
    })()})();\n;
    `;
    templateLoadersAsCode.push(templateLoaderCode);
  }
  return templateLoadersAsCode.join('');
}

export class NunjucksInlineScriptCompiler implements NunjucksCompiler {
  compile(
    templates: TemplateMetadata[],
    opts?: CompileOpts,
  ): CompiledTemplate[] {
    if (templates.length === 0) return [];

    const result: CompiledTemplate[] = [];

    const scriptSegments: string[] = [];

    templates.forEach(template => {
      try {
        const templateLoaderCode = nunjucks.precompileString(
          template.template,
          {
            name: template.name,
            wrapper: wrapperFn,
          },
        );
        scriptSegments.push(templateLoaderCode);
      } catch (error) {
        if (error instanceof Error && opts?.onError) {
          opts.onError(template.template, error);
        }
      }
    });

    if (!scriptSegments.length) {
      // if no templates were compiled successfully, we can return early without executing an empty script
      return result;
    }

    const scriptSrc = scriptSegments.join('\n');

    // execute the dynamic script to load the compiled expressions in the global object
    executeDynamicInlineScript({ scriptSrc });

    // extract the compiled functions from the global object and return them
    templates.forEach(template => {
      const compiledFn = (window as any)[WINDOW_KEY]?.[
        template.name
      ] as Function;

      // if a compiled function is not found for a template, there was an error during compilation which is handled by the onError callback, in this case we skip adding the template to the result
      if (!compiledFn) return;

      result.push({ name: template.name, compiledFn });
    });

    // clean up the global object
    delete (window as any)[WINDOW_KEY];
    return result;
  }
}
